import {defineStore} from 'pinia';
import {ref, computed} from 'vue';
import {eventSessionInvitationRepository} from "@/service/repository/eventSessionInvitationRepository";

export const useEventSessionInvitationStore = defineStore('eventSessionInvitation', () => {
	// State
	const isValidInvitation = ref(false);
	const validationReason = ref(null);
	const isLoading = ref(false);

	const invitation = ref(null);
	const session = ref(null);
	const event = ref(null);
	const venue = ref(null);
	const registrationForm = ref(null);

	const isSubmitting = ref(false);
	const registrationResult = ref(null);
	const registrationError = ref(null);

	// Getters
	// Filter out admin-only fields - those are not visible to registrants
	const formFields = computed(() => {
		const fields = registrationForm.value?.fields || [];
		return fields.filter(field => !field.adminOnly);
	});

	const maxCompanions = computed(() => invitation.value?.maxCompanions || 0);

	const formattedSessionDate = computed(() => {
		if (!session.value?.businessDate) return '';

		const date = new Date(session.value.businessDate);
		const options = {
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		};
		return date.toLocaleDateString('es-ES', options);
	});

	const formattedSessionTime = computed(() => {
		if (!session.value?.startsAt) return '';

		const startsAt = new Date(session.value.startsAt);
		const endsAt = session.value.endsAt ? new Date(session.value.endsAt) : null;

		const startTime = startsAt.toLocaleTimeString('es-ES', {
			hour: '2-digit',
			minute: '2-digit'
		});

		if (endsAt) {
			// const endTime = endsAt.toLocaleTimeString('es-ES', {
			// 	hour: '2-digit',
			// 	minute: '2-digit'
			// });
			return `${startTime}`;
		}

		return startTime;
	});

	// Actions
	function clearInvitation() {
		isValidInvitation.value = false;
		validationReason.value = null;
		invitation.value = null;
		session.value = null;
		event.value = null;
		venue.value = null;
		registrationForm.value = null;
	}

	function clearRegistration() {
		registrationResult.value = null;
		registrationError.value = null;
	}

	async function loadEventSessionInvitation(token) {
		if (!token) {
			isValidInvitation.value = false;
			validationReason.value = 'Token de invitación no proporcionado.';
			return false;
		}

		isLoading.value = true;
		clearInvitation();

		try {
			const response = await eventSessionInvitationRepository.validateInvitation(token);

			if (!response.valid) {
				isValidInvitation.value = false;
				validationReason.value = response.reason;
				return false;
			}

			isValidInvitation.value = true;
			validationReason.value = null;
			invitation.value = response.invitation;
			session.value = response.session;
			event.value = response.event;
			venue.value = response.venue;
			registrationForm.value = response.registrationForm;

			return true;
		} catch (error) {
			console.error('Error loading event session invitation:', error);
			isValidInvitation.value = false;
			validationReason.value = 'Error al cargar la invitación.';
			return false;
		} finally {
			isLoading.value = false;
		}
	}

	async function submitRegistration({token, userData, formData, companions}) {
		isSubmitting.value = true;
		clearRegistration();

		try {
			const response = await eventSessionInvitationRepository.submitRegistration({
				token,
				user: userData,
				formData,
				companions,
			});

			if (!response.success) {
				registrationError.value = response.error;
				return {success: false, error: response.error};
			}

			registrationResult.value = response;
			return response;
		} catch (error) {
			console.error('Error submitting registration:', error);
			const errorMessage = 'Error al enviar el registro. Por favor, inténtalo de nuevo.';
			registrationError.value = errorMessage;
			return {success: false, error: errorMessage};
		} finally {
			isSubmitting.value = false;
		}
	}

	return {
		// State
		isValidInvitation,
		validationReason,
		isLoading,
		invitation,
		session,
		event,
		venue,
		registrationForm,
		isSubmitting,
		registrationResult,
		registrationError,
		// Getters
		formFields,
		maxCompanions,
		formattedSessionDate,
		formattedSessionTime,
		// Actions
		clearInvitation,
		clearRegistration,
		loadEventSessionInvitation,
		submitRegistration,
	};
});

